import { ReportJSON, isDefined } from '@aloft/toolkit-shared';

import {
  COST_APPROACH_SITE_COMMENT,
  getCostApproachReplacementCostComment,
} from '../../../summaryV2/CostApproachSummary/consts.ts';
import { scalarToNumberToString } from '../../utils.ts';
import { createDirtyNode, createNodeWithText } from '../utils.ts';

export function create1004cFormNode(doc: XMLDocument, reportJSON: ReportJSON): HTMLElement {
  const form = createDirtyNode(doc, 'FORM');

  // Assume the major form is always the first form
  form.setAttribute('NUM', '1');
  form.setAttribute('FORMCODE', '1004C');
  form.setAttribute('SECCODE', '1');
  form.setAttribute('DESC', 'Manufactured Home');
  form.setAttribute('MAJOR', 'True');

  form.setAttribute('COMPSLOTS', '3');
  form.setAttribute('FIRSTCOMP', '1');

  const fields = createDirtyNode(doc, 'FIELDS');
  form.appendChild(fields);

  const allSubjectNodes = generateSubjectFieldNodes(doc, reportJSON.meta);
  fields.append(...allSubjectNodes);

  const allCostApproachNodes = generateCostApproachNodes(doc, reportJSON.subject.costApproach);
  fields.append(...allCostApproachNodes);

  const formPhotos = createDirtyNode(doc, 'FORMPHOTOS');
  form.appendChild(formPhotos);

  const attachments = createDirtyNode(doc, 'ATTACHMENTS');
  form.appendChild(attachments);

  return form;
}

function generateSubjectFieldNodes(doc: XMLDocument, meta?: ReportJSON['meta']): HTMLElement[] {
  const subjectNodes: HTMLElement[] = [];

  subjectNodes.push(createNodeWithText(doc, 'GSU_CM_OTHR1_TEXT', 'Add. Features'));
  if (isDefined(meta?.otherFeature2Label)) {
    subjectNodes.push(createNodeWithText(doc, 'GSU_CM_OTHR2_TEXT', meta?.otherFeature2Label));
  }

  return subjectNodes;
}

function generateCostApproachNodes(
  doc: XMLDocument,
  costApproach: ReportJSON['subject']['costApproach'],
): HTMLElement[] {
  const costApproachNodes: HTMLElement[] = [];

  costApproachNodes.push(createNodeWithText(doc, 'MHSTVALUESUMMARY', COST_APPROACH_SITE_COMMENT));
  costApproachNodes.push(
    createNodeWithText(
      doc,
      'CSTCOMMENTS_1',
      getCostApproachReplacementCostComment(costApproach.source?.name ?? 'Craftsman'),
    ),
  );

  costApproachNodes.push(createNodeWithText(doc, 'CST_REPRODUCTION', ''));
  costApproachNodes.push(createNodeWithText(doc, 'CST_REPLACEMENT', 'X'));

  costApproachNodes.push(createNodeWithText(doc, 'CST_DATASRC', costApproach.source?.name ?? ''));

  costApproachNodes.push(
    createNodeWithText(doc, 'CST_SERVQUAL', costApproach?.dwellingCostQuality ?? ''),
  );

  costApproachNodes.push(
    createNodeWithText(doc, 'CST_DATADATE', costApproach.source?.effectiveDate.toString()),
  );

  costApproachNodes.push(
    createNodeWithText(doc, 'CSTESTSITEVAL', scalarToNumberToString(costApproach?.site)),
  );

  costApproachNodes.push(
    createNodeWithText(doc, 'CSTESTSITEVAL', scalarToNumberToString(costApproach?.site)),
  );

  costApproachNodes.push(
    createNodeWithText(doc, 'CSTDWELLSF1', scalarToNumberToString(costApproach?.dwelling?.sqft)),
  );

  // If this is set as true ('X') then our sqft values for cost approachmwill not carry over, it will
  // be calculated based on the report's values. Even for a new report, the sqft will be blank, and
  // will not attempt to use the value we provide in the tdcx. Therefore we set it to false for now ('')
  costApproachNodes.push(createNodeWithText(doc, 'USECOSTAPPR_YES', ''));

  const numSections = costApproach.sections ?? 1;

  costApproachNodes.push(
    createNodeWithText(
      doc,
      'CSTDWELLPPSF1',
      scalarToNumberToString(costApproach?.dwelling?.costPerSqft),
    ),
  );

  costApproachNodes.push(
    createNodeWithText(
      doc,
      'CSTDWELLCOST1',
      scalarToNumberToString(costApproach?.dwelling?.totalCost),
    ),
  );

  for (let i = 1; i < numSections; i++) {
    costApproachNodes.push(
      createNodeWithText(
        doc,
        `CSTDWELLPPSF${i + 1}`,
        scalarToNumberToString(costApproach?.dwelling?.costPerSqft),
      ),
    );
  }

  // Mapped basement to the first other row, matching the cost approach table in Toolkit
  costApproachNodes.push(
    createNodeWithText(doc, 'CSTEXTRAS1', costApproach?.basement ? 'Basement' : ''),
  );
  costApproachNodes.push(
    createNodeWithText(
      doc,
      'CSTEXTRASCOST1',
      scalarToNumberToString(costApproach?.basement?.totalCost),
    ) ?? '',
  );
  costApproachNodes.push(
    createNodeWithText(
      doc,
      'CSTEXTRAS2',
      costApproach?.additionalStructures ? 'Add. Structures' : '',
    ),
  );
  costApproachNodes.push(
    createNodeWithText(
      doc,
      'CSTEXTRASCOST2',
      scalarToNumberToString(costApproach?.additionalStructures?.totalCost) ?? '',
    ),
  );

  costApproachNodes.push(
    createNodeWithText(
      doc,
      'CSTEXTRAS3',
      costApproach.garageCarport
        ? `Garage/Carport: ${costApproach?.garageCarport?.sqft.toString()} @ ${costApproach?.garageCarport?.costPerSqft.toString()}`
        : '',
    ),
  );
  costApproachNodes.push(
    createNodeWithText(
      doc,
      'CSTEXTRASCOST3',
      scalarToNumberToString(costApproach?.garageCarport?.totalCost) ?? '',
    ),
  );

  costApproachNodes.push(createNodeWithText(doc, 'CSTCOSTNEW_TEXT', ''));
  costApproachNodes.push(
    createNodeWithText(doc, 'CSTTOTCOSTNEW', scalarToNumberToString(costApproach?.new?.cost)),
  );
  costApproachNodes.push(
    createNodeWithText(doc, 'CSTMODSUBTOTAL', scalarToNumberToString(costApproach?.new?.cost)),
  );
  costApproachNodes.push(
    createNodeWithText(doc, 'CSTMULTIPLIER', scalarToNumberToString(costApproach.costMultiplier)),
  );
  // Left blank as it's only used in the depreciation 'scrapbook'
  costApproachNodes.push(createNodeWithText(doc, 'CF_CA_AGE', ''));
  costApproachNodes.push(createNodeWithText(doc, 'CF_CA_LIFESPAN', ''));

  // Depreciation Percentage and Lump Sum
  costApproachNodes.push(
    createNodeWithText(
      doc,
      'CSTPHYSDEPRLUMP',
      scalarToNumberToString(costApproach?.depreciation?.physical),
    ),
  );
  costApproachNodes.push(
    createNodeWithText(
      doc,
      'CSTFUNCDEPRLUMP',
      scalarToNumberToString(costApproach?.depreciation?.functional),
    ),
  );
  costApproachNodes.push(
    createNodeWithText(
      doc,
      'CSTEXTNDEPRLUMP',
      scalarToNumberToString(costApproach?.depreciation?.external),
    ),
  );

  costApproachNodes.push(
    createNodeWithText(doc, 'CSTSITEMKTVALUE', scalarToNumberToString(costApproach?.site)),
  );

  costApproachNodes.push(
    createNodeWithText(doc, 'CSTCOSTTOTAL', scalarToNumberToString(costApproach?.indicatedValue)),
  );

  costApproachNodes.push(
    createNodeWithText(doc, 'MHRMSAGSQFTGLA', scalarToNumberToString(costApproach.dwelling?.sqft)),
  );

  // Note: We don't have a field for the total life currently so product advised us to leave
  // GDSEFFECTAGE blank since it is subtracting 0 - GDSEFFECTAGE to set the Remaining Eco Life

  return costApproachNodes;
}
